import { matchPath } from 'react-router-dom';

import { DataCenterRoutes } from '@/routes/getSharedRoutes/getDataCenterRoutes';
import { DataCenterCategory } from '@/types/data-center';
import { generatePathWithDomain } from '@/utils/generatePathWithDomain';

export type DataCenterPage =
  | { kind: 'overview' }
  | { kind: 'ask-ai' }
  | { kind: 'custom-reports' }
  | { kind: 'category'; category: DataCenterCategory }
  | { kind: 'dashboard'; category: DataCenterCategory; dashboardId: string };

export const CATEGORY_SLUGS: Record<DataCenterCategory, string> = {
  [DataCenterCategory.Referrals]: 'referrals',
  [DataCenterCategory.Census]: 'census',
  [DataCenterCategory.Workflow]: 'workflow',
  [DataCenterCategory.Reimbursement]: 'reimbursement',
  [DataCenterCategory.Discharge]: 'discharge',
  [DataCenterCategory.ExecReporting]: 'exec-reporting'
};

export const getCategoryFromSlug = (
  slug: string | undefined
): DataCenterCategory | undefined =>
  Object.values(DataCenterCategory).find(
    (category) => CATEGORY_SLUGS[category] === slug
  );

export const parseDataCenterPage = (
  pathname: string
): DataCenterPage | null => {
  const match = (pattern: DataCenterRoutes) => matchPath(pattern, pathname);

  if (match(DataCenterRoutes.DataCenterOverview)) {
    return { kind: 'overview' };
  }
  if (match(DataCenterRoutes.DataCenterAskAi)) {
    return { kind: 'ask-ai' };
  }
  if (match(DataCenterRoutes.DataCenterCustomReports)) {
    return { kind: 'custom-reports' };
  }

  const categoryMatch = match(DataCenterRoutes.DataCenterCategory);
  const categoryFromCategoryMatch = getCategoryFromSlug(
    categoryMatch?.params.categorySlug
  );
  if (categoryFromCategoryMatch) {
    return { kind: 'category', category: categoryFromCategoryMatch };
  }

  const dashboardMatch = match(DataCenterRoutes.DataCenterDashboard);
  const categoryFromDashboardMatch = getCategoryFromSlug(
    dashboardMatch?.params.categorySlug
  );
  const dashboardId = dashboardMatch?.params.dashboardId;
  if (categoryFromDashboardMatch && dashboardId) {
    return {
      kind: 'dashboard',
      category: categoryFromDashboardMatch,
      dashboardId
    };
  }

  return null;
};

export const isDataCenterV3Path = (pathname: string): boolean =>
  parseDataCenterPage(pathname) !== null;

export const getDataCenterPagePath = (page: DataCenterPage): string => {
  switch (page.kind) {
    case 'overview':
      return generatePathWithDomain(DataCenterRoutes.DataCenterOverview);
    case 'ask-ai':
      return generatePathWithDomain(DataCenterRoutes.DataCenterAskAi);
    case 'custom-reports':
      return generatePathWithDomain(DataCenterRoutes.DataCenterCustomReports);
    case 'category':
      return generatePathWithDomain(DataCenterRoutes.DataCenterCategory, {
        categorySlug: CATEGORY_SLUGS[page.category]
      });
    case 'dashboard':
      return generatePathWithDomain(DataCenterRoutes.DataCenterDashboard, {
        categorySlug: CATEGORY_SLUGS[page.category],
        dashboardId: page.dashboardId
      });
  }
};

export const hasCareSettingChanged = <T>(
  previous: T | undefined,
  next: T | undefined
): boolean => previous !== undefined && next !== undefined && previous !== next;
